import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Copy, QrCode, Shield, CheckCircle2, Clock, XCircle, Upload } from "lucide-react";

export default function Payment() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [current, setCurrent] = useState(null);
  const [nextAmt, setNextAmt] = useState(null);
  const [activeSession, setActiveSession] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", utr: "" });
  const [screenshot, setScreenshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // A payment already consumed by a finished session (re-entry case) must not
  // block the fresh QR + UTR form — treat it as history, not "current".
  const toCurrent = (c, na, hasActive) => {
    if (!c || c.status === "NONE") return null;
    if (c.status === "APPROVED" && c.session_id && !hasActive && na?.entry_type === "reentry") return null;
    return c;
  };

  const loadAll = async () => {
    try {
      const [{ data: s }, { data: c }, { data: na }, { data: sess }] = await Promise.all([
        api.get("/payments/settings"),
        api.get("/payments/current"),
        api.get("/payments/next-amount"),
        api.get("/session/current"),
      ]);
      const hasActive = !!sess && sess.status !== "NONE";
      setActiveSession(hasActive);
      setSettings(s);
      setNextAmt(na);
      setCurrent(toCurrent(c, na, hasActive));
      if (!form.name && user?.display_name) setForm((f) => ({ ...f, name: user.display_name }));
    } catch (e) { toast.error("Could not load payment info"); }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (!current) return;
    const t = setInterval(async () => {
      try {
        const [{ data: c }, { data: sess }] = await Promise.all([
          api.get("/payments/current"),
          api.get("/session/current"),
        ]);
        const hasActive = !!sess && sess.status !== "NONE";
        setActiveSession(hasActive);
        setCurrent(toCurrent(c, nextAmt, hasActive));
      } catch {}
    }, 6000);
    return () => clearInterval(t);
  }, [current?.id]);

  const copyUPI = async () => {
    try { await navigator.clipboard.writeText(settings?.upi_id || ""); toast.success("UPI ID copied"); }
    catch { toast.error("Copy failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if ((user?.photo_ids?.length || 0) < 2) {
      toast.error("Upload at least 2 profile photos first");
      nav("/onboarding");
      return;
    }
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("mobile", form.mobile.replace(/\D/g, ""));
    fd.append("utr", form.utr.trim());
    if (screenshot) fd.append("file", screenshot);
    setBusy(true);
    try {
      await api.post("/payments/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment submitted. Awaiting admin verification.");
      await loadAll();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit payment");
    } finally { setBusy(false); }
  };

  if (!user) return null;

  const canSubmit = (user.photo_ids?.length || 0) >= 2 && !current;

  return (
    <div className="min-h-screen bg-dc-gradient flex items-start justify-center p-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-sm tracking-widest text-purple-200">← DILCONNECT</Link>

        <div data-testid="payment-card" className="glass-strong rounded-[36px] p-6 sm:p-8 mt-3">
          <div className="text-center">
            <h1 className="font-display text-2xl font-extrabold text-white">
              {nextAmt?.entry_type === "reentry" ? "Start Your New DilConnect Session" : "Start Your DilConnect Session"}
            </h1>
            <div className="mt-1 text-[11px] uppercase tracking-[0.3em] text-purple-200/70">
              {nextAmt?.entry_type === "reentry" ? "Re-entry Fee" : "Entry Fee"}
            </div>
            <div data-testid="entry-amount" className="font-display text-6xl font-black text-white mt-0.5">
              ₹{nextAmt?.amount_inr ?? settings?.amount_inr ?? 199}
            </div>
          </div>

          {nextAmt?.entry_type !== "reentry" && (
            <div data-testid="matching-process-info" className="mt-5 rounded-2xl border border-purple-400/40 bg-purple-500/10 p-4 text-left shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              <div className="flex items-center gap-2 font-display text-sm sm:text-base font-extrabold tracking-wide text-purple-100">
                <span className="text-lg">⏱️</span> 24-Hour Matching Process
              </div>
              <ul className="mt-2 space-y-1.5 text-xs sm:text-sm leading-relaxed text-purple-100/90">
                <li className="flex gap-2"><span className="text-fuchsia-300">•</span>Your matching session begins after your payment is verified.</li>
                <li className="flex gap-2"><span className="text-fuchsia-300">•</span>We aim to help you find a suitable connection within 24 hours.</li>
                <li className="flex gap-2"><span className="text-fuchsia-300">•</span><span>If no match is found, our published <Link to="/refund" className="underline text-fuchsia-200 hover:text-white">refund policy</Link> applies.</span></li>
              </ul>
            </div>
          )}

          {/* STATUS CARD */}
          {current && (
            <div data-testid="payment-status-card" className={`mt-6 rounded-2xl p-4 border ${
              current.status === "APPROVED" ? "bg-emerald-500/10 border-emerald-400/40" :
              current.status === "REJECTED" ? "bg-rose-500/10 border-rose-400/40" :
              "bg-amber-500/10 border-amber-400/40"
            }`}>
              <div className="flex items-center gap-2 font-bold">
                {current.status === "APPROVED" && <><CheckCircle2 className="w-5 h-5 text-emerald-300" /><span className="text-emerald-200">Payment Approved</span></>}
                {current.status === "REJECTED" && <><XCircle className="w-5 h-5 text-rose-300" /><span className="text-rose-200">Payment Rejected</span></>}
                {current.status === "PENDING" && <><Clock className="w-5 h-5 text-amber-300" /><span className="text-amber-200">⏳ Payment Verification Pending</span></>}
              </div>
              <div className="mt-2 text-xs text-purple-100/90">
                {current.status === "APPROVED" && "Your 24-hour matching session is active."}
                {current.status === "REJECTED" && (current.rejection_reason ? `Reason: ${current.rejection_reason}` : "Your payment could not be verified.")}
                {current.status === "PENDING" && (
                  <>
                    <div className="font-semibold text-amber-100">Your payment has been submitted successfully.</div>
                    <div className="mt-1">Please wait while our admin verifies your payment.</div>
                    <div className="mt-1 text-[11px] uppercase tracking-widest text-amber-200/80">Status · Pending — waiting for admin verification</div>
                  </>
                )}
              </div>
              <div className="mt-2 text-[11px] font-mono text-purple-200/70">Ref: {current.verification_id}</div>
              {current.status === "APPROVED" && (
                <button onClick={() => nav("/match")} className="btn-cta mt-3 w-full py-2.5 rounded-xl">Open matching</button>
              )}
              {current.status === "PENDING" && settings?.whatsapp_number && (
                <div className="mt-4 p-3 rounded-xl bg-black/30 border border-emerald-500/30">
                  <div className="text-[11px] uppercase tracking-widest text-emerald-200/90 flex items-center gap-1.5">
                    💬 Payment verification taking time?
                  </div>
                  <p className="mt-1 text-xs text-purple-100/90">If verification is taking longer, contact our admin on WhatsApp.</p>
                  <a
                    data-testid="whatsapp-support"
                    href={`https://wa.me/${(settings.whatsapp_number || "").replace(/\D/g, "")}?text=${encodeURIComponent(
                      `Hi, I have submitted my DilConnect ₹${Math.round((current.amount || 0) / 100)} payment.\nPlease verify my payment.\nName: ${current.name}\nUser ID: ${current.user_id}\nUTR: ${current.utr}`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-bold"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20.52 3.48A11.86 11.86 0 0 0 12 0C5.5 0 .2 5.3.2 11.84a11.75 11.75 0 0 0 1.6 5.94L0 24l6.37-1.67a11.86 11.86 0 0 0 5.63 1.44h.01c6.5 0 11.8-5.3 11.81-11.84a11.78 11.78 0 0 0-3.3-8.45zM12 21.6h-.01a9.78 9.78 0 0 1-4.98-1.37l-.36-.21-3.78.99 1.01-3.68-.23-.38a9.76 9.76 0 1 1 18.11-5.11c0 5.38-4.38 9.76-9.76 9.76zm5.36-7.3c-.3-.15-1.74-.86-2-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.74-1.65-2.04-.17-.3-.02-.46.13-.6.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.08-.15-.66-1.6-.91-2.19-.24-.57-.48-.49-.66-.5l-.56-.01c-.2 0-.52.08-.8.37-.27.3-1.05 1.03-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.12 3.24 5.14 4.54.72.3 1.28.48 1.72.62.72.23 1.37.2 1.88.12.57-.08 1.74-.71 1.98-1.4.25-.68.25-1.27.17-1.4-.08-.13-.27-.2-.57-.35z"/></svg>
                    Chat on WhatsApp
                  </a>
                </div>
              )}
            </div>
          )}

          {/* QR + UPI */}
          {!current && (
            <>
              <div className="mt-6 glass rounded-2xl p-4 flex flex-col items-center">
                <div className="bg-white p-3 rounded-2xl">
                  {settings?.qr_photo_id ? (
                    <img
                      data-testid="payment-qr"
                      src={`${API}/payments/qr-image`}
                      alt="UPI QR"
                      className="w-56 h-56 object-contain"
                    />
                  ) : (
                    <div className="w-56 h-56 flex items-center justify-center text-slate-400"><QrCode className="w-16 h-16" /></div>
                  )}
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-widest text-purple-200/70">Scan & Pay</div>
              </div>

              <div className="mt-4 glass rounded-2xl p-4">
                <div className="text-[11px] uppercase tracking-widest text-purple-200/70">UPI ID</div>
                <div className="mt-1 flex items-center gap-2">
                  <code data-testid="upi-id" className="flex-1 font-mono text-white text-base break-all">{settings?.upi_id || "—"}</code>
                  <button data-testid="copy-upi" onClick={copyUPI} className="px-3 py-1.5 rounded-xl bg-fuchsia-500 text-white text-xs font-bold inline-flex items-center gap-1 hover:bg-fuchsia-400">
                    <Copy className="w-3.5 h-3.5" /> Copy UPI ID
                  </button>
                </div>
                <div className="mt-3 text-[11px] text-purple-200/80">
                  Supported apps: PhonePe · Google Pay · Paytm · BHIM · any UPI app
                </div>
                <div className="mt-2 text-xs text-purple-100/80">
                  {nextAmt?.entry_type === "reentry"
                    ? `Pay ₹${nextAmt.amount_inr} using any UPI app. After paying, submit your UTR / Transaction ID below. Admin will manually verify it before activating your new 24-hour session.`
                    : (settings?.instructions || `Pay ₹${settings?.amount_inr || 199} using any UPI app. After completing the payment, submit your UTR / Transaction ID below.`)}
                </div>
              </div>

              {/* FORM */}
              <form onSubmit={submit} className="mt-5 space-y-3">
                <div className="text-[11px] uppercase tracking-widest text-purple-200/70">After paying — submit details</div>
                <input data-testid="pay-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name" className="dc-input" />
                <input data-testid="pay-mobile" required inputMode="numeric" pattern="[0-9]{10,15}" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Registered mobile number" className="dc-input" />
                <input data-testid="pay-utr" required value={form.utr} onChange={(e) => setForm({ ...form, utr: e.target.value.toUpperCase() })} placeholder="UTR / Transaction ID" className="dc-input font-mono tracking-wider" />
                <label className="block">
                  <div className="text-[11px] uppercase tracking-widest text-purple-200/70 mb-1">Payment Screenshot (recommended)</div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-2 rounded-xl bg-purple-900/60 border border-purple-500/30 text-xs inline-flex items-center gap-1.5">
                      <Upload className="w-3.5 h-3.5" /> {screenshot ? "Change file" : "Choose screenshot"}
                    </button>
                    <span className="text-xs text-purple-200/70 truncate">{screenshot?.name || "No file selected"}</span>
                  </div>
                  <input ref={fileRef} data-testid="pay-screenshot" type="file" accept="image/*" className="hidden" onChange={(e) => setScreenshot(e.target.files?.[0] || null)} />
                </label>
                <button data-testid="pay-submit" disabled={!canSubmit || busy} className="btn-cta w-full py-3 rounded-2xl disabled:opacity-50">
                  {busy ? "Submitting…" : "Submit Payment Reference"}
                </button>
                {(user.photo_ids?.length || 0) < 2 && (
                  <div className="text-xs text-amber-300 text-center">Upload at least 2 profile photos first. <Link to="/onboarding" className="underline">Finish photos</Link></div>
                )}
              </form>
            </>
          )}

          <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-purple-200/70">
            <Shield className="w-3.5 h-3.5" /> Payment is manually verified by admin · No automatic approval
          </div>
        </div>

        <div className="mt-4 text-center text-[11px] text-purple-200/50">
          <Link to="/refund" className="hover:text-white">Refund policy</Link>
          <span className="mx-2">•</span>
          <Link to="/terms" className="hover:text-white">Terms</Link>
        </div>
      </div>
      <style>{`.dc-input{width:100%;background:rgba(10,4,20,0.6);border:1px solid rgba(168,85,247,0.3);border-radius:12px;padding:10px 12px;font-size:14px;outline:none;color:#fff}
      .dc-input:focus{border-color:#c084fc;box-shadow:0 0 0 3px rgba(192,132,252,0.2)}`}</style>
    </div>
  );
}
